import { z } from 'zod';
import { FinanceFlowSchema, FinancialLineStatusSchema } from '../enums/finance.js';
import { FINANCE_BACKFILL_MAX_LEADS } from '../enums/finance-backfill.js';
import { IsoDateStringSchema } from './common.js';

/**
 * Contrats de `/finance/backfill/*` (admin). Les deux routes sont REJOUABLES :
 * relancées après un changement de grille, elles convergent (lignes d'écart
 * ou de contre-passation), elles ne suppriment jamais rien.
 */

/**
 * Booléen de query string. `z.coerce.boolean()` ferait de `"false"` un
 * `true` : on n'accepte que `true` / `false` explicites. Défaut : simulation.
 */
const QueryBooleanSchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => v === true || v === 'true');

// ── Grille historique ─────────────────────────────────────────────────

export const HistoricalTariffsBackfillQuerySchema = z.object({
  dryRun: QueryBooleanSchema.default(true),
});
export type HistoricalTariffsBackfillQuery = z.infer<typeof HistoricalTariffsBackfillQuerySchema>;

/**
 * Une version de grille dérivée de l'historique :
 *  - `create`   : à créer (créée si `dryRun=false`) ;
 *  - `exists`   : une ligne identique (même périmètre, même montant) couvre
 *                 déjà la période — rien à faire, c'est ce qui rend la route
 *                 idempotente ;
 *  - `conflict` : une autre ligne du même périmètre chevauche la période avec
 *                 un autre montant — rien n'est créé, l'admin tranche dans la
 *                 grille.
 */
export const HistoricalTariffPlanItemSchema = z.object({
  action: z.enum(['create', 'exists', 'conflict']),
  flow: z.enum(['revenue', 'payout']),
  verticaleId: z.string(),
  verticaleName: z.string().nullable(),
  deliveryPartnerId: z.string().nullable(),
  deliveryPartnerName: z.string().nullable(),
  /** Toujours nul : la reprise ne dérive que la ligne de rétrocession PAR DÉFAUT. */
  partnerId: z.null(),
  amount: z.string(),
  validFrom: IsoDateStringSchema,
  validTo: IsoDateStringSchema,
  leadCount: z.number().int(),
  firstLeadAt: IsoDateStringSchema,
  lastLeadAt: IsoDateStringSchema,
  /** Ligne existante en cause (`exists` / `conflict`). */
  existingTariffId: z.string().nullable(),
  /** Id de la ligne créée (`create`, hors simulation). */
  createdTariffId: z.string().nullable(),
});
export type HistoricalTariffPlanItem = z.infer<typeof HistoricalTariffPlanItemSchema>;

/** Périmètre où l'historique porte plusieurs montants figés : une version par valeur. */
export const HistoricalTariffMultiValueScopeSchema = z.object({
  flow: z.enum(['revenue', 'payout']),
  verticaleId: z.string(),
  deliveryPartnerId: z.string().nullable(),
  amounts: z.array(z.string()),
  versions: z.number().int(),
});

export const HistoricalTariffsBackfillResultSchema = z.object({
  runId: z.string(),
  dryRun: z.boolean(),
  /** Fin (exclue) de l'ère `per_lead`. */
  until: IsoDateStringSchema,
  items: z.array(HistoricalTariffPlanItemSchema),
  multiValueScopes: z.array(HistoricalTariffMultiValueScopeSchema),
  created: z.number().int(),
  existing: z.number().int(),
  conflicts: z.number().int(),
  /** Fiches de l'ère ignorées, faute de quoi dériver un tarif. */
  skipped: z.object({
    withoutVerticale: z.number().int(),
    withoutPrixVente: z.number().int(),
    withoutCommission: z.number().int(),
  }),
});
export type HistoricalTariffsBackfillResult = z.infer<typeof HistoricalTariffsBackfillResultSchema>;

// ── Recalcul du grand livre ───────────────────────────────────────────

/** Période sur la date de CRÉATION du lead (celle qui choisit le tarif). */
export const RecomputeBackfillQuerySchema = z
  .object({
    dryRun: QueryBooleanSchema.default(true),
    from: IsoDateStringSchema.optional(),
    to: IsoDateStringSchema.optional(),
    partnerId: z.string().min(1).max(64).optional(),
    /** Reprise après un appel plafonné : `nextCursor` de la réponse précédente. */
    cursor: z.string().min(1).max(64).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(FINANCE_BACKFILL_MAX_LEADS)
      .default(FINANCE_BACKFILL_MAX_LEADS),
  })
  .refine((v) => !v.from || !v.to || new Date(v.to) > new Date(v.from), {
    path: ['to'],
    message: '`to` doit suivre `from`',
  });
export type RecomputeBackfillQuery = z.infer<typeof RecomputeBackfillQuerySchema>;

export const RecomputeBackfillTotalSchema = z.object({
  flow: FinanceFlowSchema,
  status: FinancialLineStatusSchema,
  lines: z.number().int(),
  amount: z.string(),
});
export type RecomputeBackfillTotal = z.infer<typeof RecomputeBackfillTotalSchema>;

export const RecomputeBackfillResultSchema = z.object({
  runId: z.string(),
  dryRun: z.boolean(),
  leadsScanned: z.number().int(),
  /** Fiches dont le livre n'était pas à jour. */
  leadsChanged: z.number().int(),
  /** Lignes que le moteur ajouterait (simulation) ou a ajoutées. */
  linesPlanned: z.number().int(),
  /** Lignes réellement écrites : toujours 0 en simulation. */
  linesAppended: z.number().int(),
  totals: z.array(RecomputeBackfillTotalSchema),
  errorCount: z.number().int(),
  /** Erreurs par fiche (tronqué) : une fiche en échec n'arrête jamais le lot. */
  errors: z.array(z.object({ leadId: z.string(), message: z.string() })),
  /** Nul = périmètre épuisé. Sinon, relancer avec `cursor=nextCursor`. */
  nextCursor: z.string().nullable(),
  stoppedBy: z.enum(['end', 'limit', 'time']),
  durationMs: z.number().int(),
});
export type RecomputeBackfillResult = z.infer<typeof RecomputeBackfillResultSchema>;
